'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useState, useTransition } from 'react';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { setLocaleCookie } from '@/i18n/actions';
import { LOCALES, LOCALE_LABEL, type Locale } from '@/i18n/config';

interface Props {
  /**
   * Visual variant. Default is the icon-only header version.
   * `text` shows the full language name (Deutsch / English) and is used
   * in settings pages.
   */
  variant?: 'icon' | 'text';
}

export function LanguageSwitcher({ variant = 'icon' }: Props) {
  const locale = useLocale() as Locale;
  const t = useTranslations('language');
  const [, startTransition] = useTransition();
  const [open, setOpen] = useState(false);

  const switchTo = (next: Locale) => {
    if (next === locale) {
      setOpen(false);
      return;
    }
    startTransition(async () => {
      await setLocaleCookie(next);
      // Hard reload so RSC re-renders with the new locale messages.
      // (router.refresh() alone isn't enough — the messages were resolved
      // at the layout level using the previous cookie value.)
      window.location.reload();
    });
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={variant === 'icon' ? 'icon' : 'sm'}
          aria-label={t('label')}
          data-testid="language-switcher"
        >
          <Globe className="h-4 w-4" />
          {variant === 'text' && (
            <span className="ml-2 text-sm">{LOCALE_LABEL[locale]}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LOCALES.map((l) => (
          <DropdownMenuItem
            key={l}
            data-testid={`language-option-${l}`}
            onClick={() => switchTo(l)}
            className={l === locale ? 'font-semibold' : undefined}
          >
            {LOCALE_LABEL[l]}
            {l === locale && <span className="ml-2 text-muted-foreground">✓</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
